import type {
  AnnouncementDisplayFrequency,
  AnnouncementMessageType,
} from '@/apiClient/types/announcements'

/**
 * The admin-facing label for each announcement message type.
 *
 * Header artwork lives in `ModalAnnouncement/headerIllustrations` and reaches
 * the modal through `UIModal`'s `headerImageSrc`, so nothing here needs to
 * describe how a type looks — only what it is called.
 */
export interface AnnouncementMessageTypeMeta {
  value: AnnouncementMessageType
  /** Copy shown in the admin "Message type" select. */
  label: string
}

export const ANNOUNCEMENT_MESSAGE_TYPE_META: Record<
  AnnouncementMessageType,
  AnnouncementMessageTypeMeta
> = {
  warning: {
    value: 'warning',
    label: 'Warning',
  },
  critical: {
    value: 'critical',
    label: 'Critical',
  },
  new_feature: {
    value: 'new_feature',
    label: 'New feature',
  },
  info: {
    value: 'info',
    label: 'Info',
  },
  success: {
    value: 'success',
    label: 'Success',
  },
  custom: {
    value: 'custom',
    label: 'Custom',
  },
}

/**
 * Types withheld from the admin select.
 *
 * `custom` is parked: its header image URL was only ever validated with an
 * advisory warning, so a announcement could go live with a header that never
 * loaded or was blocked as mixed content. Withholding the option removes the
 * failure mode rather than hardening it.
 *
 * Deliberately a filter over the metadata rather than a deletion from it —
 * reading stays tolerant, so an announcement already stored as `custom` still
 * renders — the modal reads its header from the illustrations, not from this
 * list. To bring the option back, empty this list.
 */
const WITHHELD_MESSAGE_TYPES: readonly AnnouncementMessageType[] = ['custom']

/** Ordered for the admin select — matches the design's dropdown order. */
export const ANNOUNCEMENT_MESSAGE_TYPE_ITEMS = Object.values(
  ANNOUNCEMENT_MESSAGE_TYPE_META,
)
  .filter(({ value }) => !WITHHELD_MESSAGE_TYPES.includes(value))
  .map(({ value, label }) => ({ value, label }))

/** Fallback copy for the modal's primary button when the admin leaves it blank. */
export const DEFAULT_ANNOUNCEMENT_BUTTON_LABEL = 'Got it'

/** Matches the design's `0/20` counter under the Button label field. */
export const ANNOUNCEMENT_BUTTON_LABEL_MAX_LENGTH = 20

export const ANNOUNCEMENT_NAME_MAX_LENGTH = 60

/**
 * Cap on the body's **text** content, markup excluded (epic Risk 10 — long copy
 * breaking the card layout). Counted on the text rather than the HTML because
 * markup weight tracks formatting, not how much there is to render: a byte cap
 * would pass a plain wall of text and reject a short, heavily formatted one.
 * Mirrored server-side on write — see EVGPT-1626.
 */
export const ANNOUNCEMENT_BODY_MAX_LENGTH = 1500

/** "Display duration" select. `'none'` = stays live until an admin unpublishes it. */
export const ANNOUNCEMENT_DURATION_ITEMS = [
  { value: '1', label: '1 day' },
  { value: '3', label: '3 days' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: 'none', label: 'No expiry' },
]

export const durationToSelectValue = (days: number | null): string =>
  days === null ? 'none' : String(days)

export const selectValueToDuration = (value: string): number | null =>
  value === 'none' ? null : Number(value)

/**
 * "Display frequency" radio group.
 *
 * Typed rather than left to inference: the radio group hands its value back as
 * a bare string, and without this the cast at that boundary would let a typo
 * reach `contentToWire` and silently fall through to its guard.
 *
 * "Show once per session" leads because it is the default — see
 * `ANNOUNCEMENT_CONTENT_DEFAULTS` — and a radio group whose first option is not
 * the pre-selected one reads as though something has already been chosen for
 * the admin.
 */
export const ANNOUNCEMENT_FREQUENCY_ITEMS: {
  value: AnnouncementDisplayFrequency
  label: string
}[] = [
  { value: 'once_per_session', label: 'Show once per session' },
  { value: 'until_dismissed', label: 'Show until dismissed' },
  { value: 'once', label: 'Show once' },
  { value: 'every_7_days', label: 'Show every 7 days' },
]

/**
 * What a freshly created announcement starts from lives with the wire mapping
 * — `ANNOUNCEMENT_CONTENT_DEFAULTS` in `apiClient/modules/announcementMapping`
 * — because the create call has to send it in full: the API has no per-field
 * defaults of its own.
 */
